import { DOCUMENT } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    Input,
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { Router } from "@angular/router";

import "@angular/localize/init";

import {
    ImageAspectRatio,
    LINK,
    NONE,
    RADIO,
} from "@hermes/aphrodite/dynamic-box";
import {
    AbstractProductVariant,
    Product,
    ProductFinishVariant,
    ProductSizeVariant,
    ProductStock,
    ProductVolumeVariant,
    ProductColorVariant,
    ProductBuckleVariant,
    BoxType,
} from "@hermes/api-model-product";

import {
    isOnlineAndRetailStock,
    isOnlineOnlyStock,
    isRetailOnlyStock,
    isDisplayOnly,
    isOutOfStock,
} from "@hermes/fragments/product-utils";
import {
    BUCKLE,
    COLOR,
    FINISH,
    FetchProductService,
    LEATHER,
    ProductPageFacade,
    ProductVariantType,
    SIZE,
    STRAP,
    STRAP_SIZE,
    VOLUME,
} from "@hermes/states/product-page";
import { skuToUrlSku } from "@hermes/utils-generic/helpers";

/**
 * This component manage a variant item
 */
@Component({
    selector: "h-variant-item",
    templateUrl: "./variant-item.component.html",
    styleUrls: ["./variant-item.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VariantItemComponent {
    @Input()
    public formGroup!: FormGroup;

    @Input()
    public imageSrc!: string;

    @Input()
    public imageAspectRatio?: ImageAspectRatio;

    @Input()
    public length!: number;

    @Input()
    public formName!: string;

    @Input()
    public variantType!: ProductVariantType;

    @Input()
    public inputId!: string;

    @Input()
    public hasFixedWidth: boolean = false;

    @Input()
    public routing: boolean = false;

    @Input()
    public hasSizeVariants!: boolean;

    @Input()
    public areAllSizeVariantsOutOfStock!: boolean;

    @Input()
    public currentProduct!: Product;

    @Input()
    public allVariantsOutOfStock?: boolean;

    // Variation stock
    public stock!: ProductStock;

    public focused!: boolean;

    public isOutOfStock!: boolean;
    public isOnlineOnlyStock!: boolean;
    public isRetailOnlyStock!: boolean;
    public isOnlineAndRetailStock!: boolean;
    public isDisplayOnly!: boolean;

    public boxValue!: string;

    public productAvailabilityStatus!: string;
    public labelValue!: string;
    public titleValue!: string;

    public checked: boolean = false;

    private inputVariant: AbstractProductVariant = new AbstractProductVariant();
    private inputBoxType: BoxType = NONE;
    private inputSelectedValue: string = "";

    constructor(
        private router: Router,
        private productPageFacade: ProductPageFacade,
        private fetchProductService: FetchProductService,
        @Inject(DOCUMENT) private document: Document,
        public changeDetector: ChangeDetectorRef,
    ) {}

    @Input()
    public set variant(variant: AbstractProductVariant) {
        this.inputVariant = variant;
        if (this.inputBoxType !== NONE) {
            this.initVariant();
        }
    }
    public get variant(): AbstractProductVariant {
        return this.inputVariant;
    }

    @Input()
    public set selectedValue(selectedValue: string) {
        this.inputSelectedValue = selectedValue;
        this.initVariant();
    }
    public get selectedValue(): string {
        return this.inputSelectedValue;
    }

    @Input()
    public set boxType(boxType: BoxType) {
        this.inputBoxType = boxType;
        if (this.inputVariant.sku) {
            this.initVariant();
        }
    }
    public get boxType(): BoxType {
        return this.inputBoxType;
    }

    // ----- User interactions

    public handleSelectVariant(): void {
        if (this.variantType === VOLUME) {
            this.fetchProductService.isVariantVolumeChanged$.next(true);
        }
        if (this.boxType === RADIO && this.routing) {
            this.router.navigate(this.buildRouteArray(), {
                state: { variant: true },
            });
        }
        if (this.boxType === LINK) {
            this.productPageFacade.selectVariant(this.variantType, {
                ...this.variant,
                boxType: this.boxType,
            });
            this.updateChecked();
        }
    }

    /**
     * Notify state of functional value change.
     */
    public handleChange(): void {
        if (this.boxType === RADIO && this.routing) {
            this.productPageFacade.selectVariant(this.variantType, {
                ...this.variant,
                boxType: this.boxType,
            });
        }
    }

    public handleFocus(): void {
        if (this.boxType === RADIO) {
            this.focused = true;
            this.changeDetector.markForCheck();
        }
    }

    public handleBlur(): void {
        if (this.boxType === RADIO) {
            this.focused = false;
            this.changeDetector.markForCheck();
        }
    }

    /**
     * return true if size not selected alert is display
     */
    public isSizeMessageAlertDisplay(): boolean {
        return Boolean(this.document.querySelector(".size-alert-container"));
    }

    private getAvailabilityStatusTraduction(): string {
        switch (true) {
            case this.isDisplayOnly: {
                return $localize`:@@hermes-availability.product-unavailable:Unfortunately this product is no longer available`;
            }
            case this.isOutOfStock: {
                return $localize`:@@page-hermes-product-locator-accessibility-no-stock:no stock`;
            }
            case this.isRetailOnlyStock: {
                return $localize`:@@page-hermes-product-locator-accessibility-stock-retail-only:available in store only`;
            }
            case this.isOnlineAndRetailStock: {
                return $localize`:@@page-hermes-product-locator-accessibility-hcom-and-stock-retail:available online and in store`;
            }
            case this.isOnlineOnlyStock: {
                return $localize`:@@page-hermes-product-locator-accessibility-stock-hcom-only:available online only`;
            }
            default: {
                return "";
            }
        }
    }

    private getTitleValue(): string {
        const productTitle = this.currentProduct?.title || "";
        const measureUnit =
            this.currentProduct?.simpleAttributes?.measureUnit || "";
        const availability = this.productAvailabilityStatus || "";
        const label = this.labelValue || "";

        // For links we need to add title and display availability only for out of stock.
        if (this.boxType === LINK || this.boxType === NONE) {
            const base =
                this.variantType === VOLUME
                    ? `${productTitle}, ${label} ${measureUnit}`
                    : `${productTitle}, ${label}`;
            return this.isOutOfStock ? `${base}, ${availability}` : base;
        }

        return this.isOutOfStock ? `${label}, ${availability}` : "";
    }

    /**
     * Build route array from variant attributes,
     * Manages edge cases with slash or spaces in sku
     */
    private buildRouteArray(): string[] {
        const skuParts = skuToUrlSku(this.variant.sku).split("/");
        const routes: string[] = [
            "product",
            `${this.variant.slug}-${skuParts.shift()}`,
        ];
        if (skuParts.length > 0) {
            routes.push(...skuParts);
        }
        // slash at the end with empty string
        return [...routes, ""];
    }

    /**
     * checked true if :
     * - this variant correspond to the selected variant
     * - this variant has stock
     */
    private updateChecked(): void {
        if (this.variantType) {
            let valueVariant: string = "";
            switch (this.variantType) {
                case SIZE:
                case STRAP_SIZE: {
                    valueVariant = (this.inputVariant as ProductSizeVariant)
                        .size;
                    break;
                }
                case FINISH: {
                    valueVariant = (this.inputVariant as ProductFinishVariant)
                        .itemSku;
                    break;
                }
                case VOLUME: {
                    valueVariant = (this.inputVariant as ProductVolumeVariant)
                        .volume;
                    break;
                }
            }
            this.checked = this.inputSelectedValue === valueVariant;
            if (this.checked && this.boxType === RADIO) {
                this.boxValue = this.getBoxValue(valueVariant);
                this.formGroup.controls[this.formName].setValue(this.boxValue);
            }
        }
    }

    private updateVariantValues() {
        switch (this.variantType) {
            case SIZE:
            case STRAP_SIZE: {
                const productSizeVariant = this
                    .inputVariant as ProductSizeVariant;
                this.labelValue = productSizeVariant.size;
                break;
            }
            case VOLUME: {
                const productVolumeVariant = this
                    .inputVariant as ProductVolumeVariant;
                this.labelValue = productVolumeVariant.volume;
                break;
            }
            case COLOR:
            case STRAP:
            case LEATHER: {
                const productColorVariant = this
                    .inputVariant as ProductColorVariant;
                this.labelValue = productColorVariant.color;
                break;
            }
            case BUCKLE: {
                const productColorVariant = this
                    .inputVariant as ProductBuckleVariant;
                this.labelValue = `${productColorVariant.displayNameList} ${productColorVariant.color}`;
                break;
            }
            case FINISH: {
                const productFinishVariant = this
                    .inputVariant as ProductFinishVariant;
                this.labelValue = productFinishVariant.finish;
                break;
            }
        }

        this.titleValue = this.getTitleValue();
        this.boxValue = this.getBoxValue(this.labelValue);

        // Update form's variant type with the selected sku variation.
        // Used for A11Y to force checked input
        if (this.boxType === RADIO && this.formGroup && this.checked) {
            this.formGroup
                .get(this.variantType)
                ?.setValue(this.inputVariant.sku);
        }
    }

    private getBoxValue(variantValue: string): string {
        // when routing is true (for simple product), value is the sku, used when add to cart
        // when routing is false, used to know the selected variant
        return this.routing ? this.inputVariant.sku : variantValue;
    }

    private initVariant(): void {
        // Check if the functional value matches, and force / update the form according to it.
        if (this.inputVariant) {
            if (this.inputVariant?.stock) {
                this.stock = this.inputVariant.stock;
            } else {
                // If there is no stock information, consider there is no stock
                this.stock = new ProductStock();
                this.stock.retail = false;
                this.stock.ecom = false;
            }

            this.isOutOfStock =
                this.allVariantsOutOfStock === undefined
                    ? isOutOfStock(this.stock)
                    : this.allVariantsOutOfStock;
            this.isOnlineOnlyStock = isOnlineOnlyStock(this.stock);
            this.isRetailOnlyStock = isRetailOnlyStock(this.stock);
            this.isOnlineAndRetailStock = isOnlineAndRetailStock(this.stock);
            this.isDisplayOnly = isDisplayOnly(this.stock);
            this.updateChecked();
            this.productAvailabilityStatus =
                this.getAvailabilityStatusTraduction();
            this.updateVariantValues();
        }
        if (this.inputSelectedValue) {
            this.updateChecked();
        }
        this.changeDetector.markForCheck();
    }
}
